'use client';
import { useEffect, useRef, useState } from 'react';

/**
 * useMediaQuery subscribes to media queries. It receives a media query as an argument and returns true if the given media query matches the current state.
 * The hook relies on window.matchMedia() API and will return false if the API is not available, unless an initial value is provided in the second argument.
 * Resize browser window to trigger window.matchMedia event.
 * @param query - The media query to match.
 * @param initialValue - The initial value of the media query.
 * @param options - The options for the hook.
 * @param options.getInitialValueInEffect - Whether to get the initial value in the effect. Defaults to true.
 *
 * @returns - The result of the media query.
 * @classdesc
 * useMediaQuery is a hook that subscribes to media queries. It receives a media query as an argument and returns true if the given media query matches the current state.
 * ```tsx
 * function useMediaQuery(
        query: string,
        initialValue?: boolean,
        options?: {
            getInitialValueInEffect: boolean;
        }
    ): boolean;
 * ```
 *
 * @example
 * ```tsx
    import { useMediaQuery } from '@intigral/hooks';

    function Demo() {
        const matches = useMediaQuery('(min-width: 56.25em)');

        return (
            <Badge color={matches ? 'teal' : 'red'} variant="filled">
                Breakpoint {matches ? 'matches' : 'does not match'}
            </Badge>
        );
    }
    ```
    Server Side Rendering
    During server side rendering the hook will always return false as window.matchMedia api is not available,
    if that is not a desired behavior, you can override the initial value:
    ```tsx
        import { useMediaQuery } from '@intigral/hooks';

        function Demo() {
            // Set initial value in second argument and getInitialValueInEffect option to false
            const matches = useMediaQuery('(max-width: 40em)', true, {
                getInitialValueInEffect: false,
            });
        }
    ```
*/

/**
 * UseMediaQueryOptions type
    package exports UseMediaQueryOptions type – a type of the second argument of useMediaQuery hook.
    @example
    ```tsx
    import { useMediaQuery, UseMediaQueryOptions } from '@mantine/hooks';
    const options: UseMediaQueryOptions = {
        getInitialValueInEffect: false,
    };
    const matches = useMediaQuery('(max-width: 40em)', true, options);
    ```
 */
export interface UseMediaQueryOptions {
    getInitialValueInEffect: boolean;
}

type MediaQueryCallback = (event: { matches: boolean; media: string }) => void;

/**
 * Older versions of Safari (shipped withCatalina and before) do not support addEventListener on matchMedia
 * https://stackoverflow.com/questions/56466261/matchmedia-addlistener-marked-as-deprecated-addeventlistener-equivalent
 * */
function attachMediaListener(query: MediaQueryList, callback: MediaQueryCallback) {
    try {
        query.addEventListener('change', callback);
        return () => query.removeEventListener('change', callback);
    } catch (e) {
        query.addListener(callback);
        return () => query.removeListener(callback);
    }
}

function getInitialValue(query: string, initialValue?: boolean) {
    if (typeof initialValue === 'boolean') {
        return initialValue;
    }

    if (typeof window !== 'undefined' && 'matchMedia' in window) {
        return window.matchMedia(query).matches;
    }

    return false;
}

export function useMediaQuery(
    query: string,
    initialValue?: boolean,
    { getInitialValueInEffect }: UseMediaQueryOptions = {
        getInitialValueInEffect: true,
    }
) {
    const [matches, setMatches] = useState(getInitialValueInEffect ? initialValue : getInitialValue(query));
    const queryRef = useRef<MediaQueryList | undefined>(undefined);

    useEffect(() => {
        if ('matchMedia' in window) {
            queryRef.current = window.matchMedia(query);
            setMatches(queryRef.current.matches);
            return attachMediaListener(queryRef.current, (event) => setMatches(event.matches));
        }

        return undefined;
    }, [query]);

    return matches;
}
